import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Autocomplete, Box, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { TimerContext } from '../context/TimerContext';
import { getAllFilteredTask } from '../../../services/taskService';
import { GroupHeader, GroupItems } from '../styles/timeWrapperStyles';
import { useStyles } from '../styles/taskSelectorStyles';
import { taskSelectorOptions } from '../constants/defaultValues';
import { AddTaskModal, TaskField } from '.';
import ProgressBar from '../../../components/progressbar/ProgressBar';

function TaskSelector({ initialLoading, fetchTaskListData }) {
  const classes = useStyles();

  const { isRunning, selectedTask, setSelectedTask, selectedProject, startTime } = useContext(TimerContext);

  const [open, toggleOpen] = useState(false);

  const [taskList, setTaskList] = useState([]);
  const [defaultTaskName, setDefaultTaskName] = useState('');

  const accountData = useSelector((state) => state?.login?.user || null);
  const userId = accountData?.id;

  const handleClose = () => {
    setSelectedTask(undefined);
    toggleOpen(false);
  };

  const handleChange = (_, newValue) => {
    if (typeof newValue === 'string') {
      // timeout to avoid instant validation of the dialog's form.
      setTimeout(() => {
        toggleOpen(true);
        setDefaultTaskName(newValue);
        setSelectedTask(undefined);
      });
      setSelectedTask(undefined);
    } else if (newValue && newValue.inputValue) {
      toggleOpen(true);
      setDefaultTaskName(newValue.inputValue);
      setSelectedTask(undefined);
    } else {
      setSelectedTask(newValue);
    }
  };

  const fetchTaskData = useCallback(async () => {
    try {
      const myTask = await getAllFilteredTask({ pageSize: 'all' });
      const filteredTasks = myTask?.data?.rows?.filter(
        (taskList) =>
          taskList?.userId === userId &&
          (taskList?.status === 'To Do' || taskList?.status === 'In Progress' || taskList?.status === 'Not Started')
      );
      setTaskList(filteredTasks);
    } catch (e) {
      toast.error(e.message);
    }
  }, [userId]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isRunning && selectedTask && startTime) {
      const interval = setInterval(() => {
        // Calculate the time increment for 10 minutes
        const increment = 10 / 60; // 10 minutes in hours (10 minutes = 10/60 hours)

        setSelectedTask((prevTask) => ({
          ...prevTask,
          consumedHours: Number(prevTask.consumedHours) + increment,
        }));
      }, 10 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isRunning, selectedTask, startTime, setSelectedTask]);

  if (initialLoading) return <CircularProgress sx={{ mb: 2.5 }} />;

  return (
    <Box className={classes.root}>
      <Autocomplete
        className={isRunning ? 'stopTime' : 'startTime'}
        value={selectedTask}
        options={taskList}
        disabled={isRunning}
        onChange={handleChange}
        renderInput={(params) => <TaskField {...params} fetchTaskListData={fetchTaskListData} />}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        {...taskSelectorOptions}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2.5,
          width: '100%',
        }}
      >
        <button
          id="fade-button"
          type="button"
          className={isRunning ? 'currentProject' : 'addProject'}
          style={{ margin: '0px' }}
        >
          {selectedProject?.client && selectedProject?.project ? (
            <Box className={classes.taskDetails}>
              <Typography className={classes.taskDetailsText}>{selectedProject?.project}</Typography>
            </Box>
          ) : (
            <Typography className={classes.taskDetailsText}>Project</Typography>
          )}
        </button>

        {selectedTask && isRunning && (
          <Box sx={{ width: '60%', pt: 0.5 }}>
            <ProgressBar
              maxValue={selectedTask?.plannedHours}
              value={Number(selectedTask?.consumedHours).toFixed(2)}
              plannedValue={selectedTask?.plannedHours}
              height="13px"
              fontSize="12px"
            />
          </Box>
        )}
      </Box>
      <AddTaskModal open={open} onClose={handleClose} defaultTaskName={defaultTaskName} fetchTaskData={fetchTaskData} />
    </Box>
  );
}

TaskSelector.propTypes = {
  initialLoading: PropTypes.bool.isRequired,
};

export default TaskSelector;
